/**
 * Wire types for the new 3-step native/Unity dependency API (`/v4/android`, `/v4/ios`,
 * `/v4/unity`) and the SPM consistency validator (`/v4/spm/coverage`) — SDK-562 Phase 6
 * (Requirement W).
 *
 * Mirrored 1:1 from the backend DTOs in
 * `com.appodeal.wizard.integration.models.{NativeModels,UnityModels,SpmDependencyDto,SpmConsistency}`.
 * Field names here are camelCase — the `humps` Axios interceptor (`config/_axios_config.ts`)
 * camelizes every response body before it reaches application code (e.g. `display_name` →
 * `displayName`), so these types describe the POST-conversion shape, not the wire's snake_case.
 */

/** Badge type constants — mirrors `Badge.companion` in `UnityModels.kt`. */
export enum BadgeType {
  RECOMMENDED = 1,
  DEPRECATED = 2,
  UNSTABLE = 3,
  NEW = 4,
}

export interface Badge {
  type: number;
  message?: string;
}

export interface VersionInfo {
  id: number;
  name: string;
  /**
   * Optional, not just "possibly empty": the backend's API `Json` instance sets
   * `encodeDefaults = false` (`JsonConfig.kt`), so a `VersionInfo` with no badges omits the
   * `badges` key entirely rather than sending `[]` — `Badge.badges: List<Badge> = emptyList()`
   * in `UnityModels.kt` never survives serialization when it equals its default. Every read site
   * must treat this as `undefined`, not just falsy-empty; do not "tidy" the `?` away.
   */
  badges?: Badge[];
  mediations?: string[];
  message?: string;
}

/** API category codes — mirrors `SdkCategory.toApiValue()`: 1=Mediation, 2=AdNetwork, 3=Service. */
export enum NativeSdkCategory {
  OTHER = 0,
  MEDIATION = 1,
  AD_NETWORK = 2,
  SERVICE = 3,
}

/** API requirement codes — mirrors `Requirement.toApiValue()`: 1=Default, 2=Required, 3=Optional. */
export enum NativeRequirement {
  DEFAULT = 1,
  REQUIRED = 2,
  OPTIONAL = 3,
}

export interface MediationInfo {
  name: string;
  displayName: string;
  category: number;
  requirement: number;
  versions: VersionInfo[];
}

export interface SdkInfo extends MediationInfo {
  adTypes?: number;
  warning?: string;
}

/** Step 1 response, Android/iOS: `GET /v4/{platform}/{version}/mediations`. */
export interface NativeMediationsResponse {
  mediations: MediationInfo[];
}

/** Step 2 response, Android/iOS: `POST /v4/{platform}/{version}/sdks`. */
export interface NativeSdksResponse {
  sdks: SdkInfo[];
}

/** Step 1 response, Unity: `GET /v4/unity/{version}/mediations` — dual-platform, unlike native. */
export interface UnityMediationsResponse {
  android: MediationInfo[];
  ios: MediationInfo[];
}

/** Step 2 response, Unity: `POST /v4/unity/{version}/sdks`. */
export interface UnitySdksResponse {
  android: SdkInfo[];
  ios: SdkInfo[];
}

/** Selection payload shared by Step 2 (`/sdks`) and Step 3 (`/dependencies`) on Android/iOS. */
export interface PlatformSelection {
  mediations: number[];
  networks: number[];
  services: number[];
}

export const emptyPlatformSelection = (): PlatformSelection => ({
  mediations: [],
  networks: [],
  services: [],
});

/** Unity's dual-platform selection wrapper — also what `POST /v4/spm/coverage` accepts (O1). */
export interface SdksRequest {
  android: PlatformSelection;
  ios: PlatformSelection;
}

/** `integration_type` query param (Requirement B2) — iOS only. Wire name stays snake_case. */
export type IntegrationType = "cocoapods" | "spm";

/**
 * Flat iOS SPM dependency shape (T5 / Confluence §6.2): `GET /v4/ios/{version}/dependencies` with
 * `integration_type=spm` responds with a bare JSON array of these — no wrapper, no `replacesPod`
 * (that field is a materialization/admin concern, see `types/spmIntegrationInfo.ts`).
 */
export interface SpmDependency {
  url: string;
  version: string;
  product: string;
}

/** Rule V1 result — `SpmConsistency.kt#CoreSplitResult`. */
export interface CoreSplitResult {
  isSplit: boolean;
  coreSdkId?: number;
  adaptersWithAnchor: number;
  nonCoreAnchorCount: number;
  message: string;
}

/**
 * One entity Rule V2 counted as *uncovered* (no `spm_integration_info_v1` anchor at all) —
 * mirrors `SpmConsistency.kt#UncoveredEntity`. See that KDoc for what `status` means per `kind`
 * (an adapter has no name column of its own; its `status` is resolved via
 * `adapter_to_sdk_pivots_v1` -> `sdks_v1.status`).
 *
 * `kind` no longer includes `"network_service_sdk"` — the backend never emits that variant any
 * more. Network/service SDKs are covered transitively through their bridge adapters, so a network
 * SDK never itself lacked an anchor in a way worth naming here; entities that are simply outside
 * SPM's reach are reported separately via `StoreCoverageReport.outOfScopeNetworks` below.
 */
export interface UncoveredEntity {
  id: number;
  status: string;
  version: string;
  kind: "mediation_sdk" | "adapter";
}

/**
 * Why a network SDK is excluded from SPM scope entirely — mirrors
 * `SpmConsistency.kt#OutOfScopeReason`. Wire values are the literal snake_case strings (`humps`
 * only camelizes object keys, never string values — see `OutOfScopeNetwork` below).
 *
 * - `"no_spm_package"`: no `spm_integration_info_v1` row exists for this network at ANY version.
 *   Not fixable by changing the current selection.
 * - `"not_in_selection"`: an SPM package exists for this network, just not at the version
 *   currently selected.
 */
export type OutOfScopeReason = "no_spm_package" | "not_in_selection";

/**
 * One network SDK the coverage check excluded from Rule V2 entirely — mirrors
 * `SpmConsistency.kt#OutOfScopeNetwork`. Distinct from `UncoveredEntity`: these are never counted
 * in `totalAdapters`/`adaptersWithAnchor` at all, so they would otherwise be invisible to the
 * panel — see `reason`'s KDoc for the two cases.
 */
export interface OutOfScopeNetwork {
  id: number;
  status: string;
  reason: OutOfScopeReason;
}

/**
 * Rule V2 result — `SpmConsistency.kt#StoreCoverageReport`. The four `total*`/`*WithAnchor` fields
 * are counts; `uncovered` is a bounded (`UNCOVERED_DETAIL_CAP = 100`,
 * `SpmConsistencyValidator.kt`) detail list naming which entities make up the gap between a
 * `total*` and its `*WithAnchor` counterpart — populated only for the selection-scoped coverage
 * check, always empty for the global/store-wide report. `outOfScopeNetworks` names networks
 * excluded from the ratio altogether (see `OutOfScopeNetwork` above). All three list/flag fields
 * are optional here (rather than required) so a stale backend that predates them, or a backend
 * that omits an empty list under `encodeDefaults = false` (`JsonConfig.kt`), degrades to "no
 * detail available" instead of a runtime shape mismatch — read with `?? []`, never assume present.
 *
 * `totalNetworkServiceSdks`/`networkServiceSdksWithAnchor` were removed: they no longer exist on
 * the wire. Network/service SDKs are covered transitively via their bridge adapters, and the panel
 * used to render that always-0 ratio as if it were a gap — see `IntegrationPreview.tsx`'s
 * `ValidatorPanel` for the corresponding UI removal.
 */
export interface StoreCoverageReport {
  totalMediationSdks: number;
  mediationSdksWithAnchor: number;
  totalAdapters: number;
  adaptersWithAnchor: number;
  uncovered?: UncoveredEntity[];
  uncoveredTruncated?: boolean;
  outOfScopeNetworks?: OutOfScopeNetwork[];
}

/**
 * Android is always `null` on the backend (`SpmCoreSplitByPlatform.android/ios: CoreSplitResult? =
 * null`, `SpmConsistency.kt`) — there is no SPM concept on Android. Both fields are optional, not
 * `| null`: `encodeDefaults = false` (`JsonConfig.kt`) omits a property from the payload entirely
 * when it equals its declared default, so a `null` value arrives as `undefined`, not `null`. Same
 * class of bug as the `badges` fix above — do not "tidy" the `?` away.
 */
export interface SpmCoreSplitByPlatform {
  android?: CoreSplitResult;
  ios?: CoreSplitResult;
}

/**
 * `POST /v4/spm/coverage` response (O1). `v1` is `undefined` (not `null`) when the request body was
 * empty (global V2-only report) — same `encodeDefaults = false` omission as [SpmCoreSplitByPlatform]
 * above (`SpmCoverageResponse.v1: SpmCoreSplitByPlatform? = null`, `SpmConsistency.kt`); present
 * when a selection was sent.
 */
export interface SpmCoverageResponse {
  v1?: SpmCoreSplitByPlatform;
  v2: StoreCoverageReport;
}
